import React from 'react';

/**
 * AiSummaryItem Component
 * Renders an individual natural-language briefing statement adhering to:
 * [Project/Task/Activity] + what happened + relevant context + why it matters / potential next step.
 * Clickable project badge and action buttons deep-link into existing ERP workflows.
 */
export default function AiSummaryItem({ item, onItemActionClick }) {
    if (!item) return null;

    const getCategoryStyles = (color) => {
        switch (color) {
            case 'amber':
                return {
                    dot: 'bg-amber-500',
                    badge: 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 hover:bg-amber-200/70 dark:hover:bg-amber-900/60',
                    actionBtn: 'text-amber-700 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200',
                };
            case 'emerald':
                return {
                    dot: 'bg-emerald-600 dark:bg-emerald-500',
                    badge: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/60',
                    actionBtn: 'text-emerald-700 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-200',
                };
            case 'purple':
                return {
                    dot: 'bg-purple-600 dark:bg-purple-500',
                    badge: 'bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60 hover:bg-purple-200/70 dark:hover:bg-purple-900/60',
                    actionBtn: 'text-purple-700 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-200',
                };
            case 'blue':
            default:
                return {
                    dot: 'bg-blue-600 dark:bg-[#00AAF1]',
                    badge: 'bg-blue-100/70 dark:bg-blue-950/50 text-[#03488A] dark:text-[#00AAF1] border border-blue-200/60 dark:border-blue-800/60 hover:bg-blue-200/70 dark:hover:bg-blue-900/60',
                    actionBtn: 'text-blue-600 hover:text-[#03488A] dark:text-[#00AAF1] dark:hover:text-blue-300',
                };
        }
    };

    const styles = getCategoryStyles(item.categoryColor);

    return (
        <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-300 leading-relaxed group">
            {/* Category Indicator Dot */}
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${styles.dot} mt-2 flex-shrink-0 transition-transform group-hover:scale-125`} />

            {/* Content & Deep Links */}
            <div className="flex-1">
                {/* Clickable Project / Entity Badge */}
                <button
                    onClick={() => onItemActionClick?.(item)}
                    className={`inline-flex items-center gap-1 font-bold ${styles.badge} px-2 py-0.5 rounded mr-1.5 transition-colors cursor-pointer text-left shadow-2xs`}
                    title={`Click to inspect ${item.projectName}`}
                >
                    <span className="material-symbols-outlined text-[11px]">{item.icon || 'folder'}</span>
                    <span>{item.projectName}</span>
                </button>

                {/* AI-Generated Natural Narrative (Concise Headline for Banner) */}
                <span className="text-slate-700 dark:text-slate-300 font-normal">
                    {item.headline || item.text}
                </span>

                {/* Clickable Action Deep-Link Button */}
                {item.actionLabel && (
                    <button
                        onClick={() => onItemActionClick?.(item)}
                        className={`font-bold ml-1.5 underline underline-offset-2 inline-flex items-center gap-0.5 cursor-pointer transition-colors ${styles.actionBtn}`}
                    >
                        [{item.actionLabel}{' '}
                        <span className="material-symbols-outlined text-[11px]">arrow_forward</span>]
                    </button>
                )}
            </div>
        </div>
    );
}
